/* eslint-disable no-param-reassign */
import React from 'react';
import { isArray, orderBy } from 'lodash';
/* eslint-disable-next-line no-unused-vars */
import { Checkbox, Radio } from '../ThirdParty';
import {
  availableAsset,
  availableInventoryAsset,
  assetSummary,
  sortOrder,
  sortOrderInventory,
  EXCLUDED_STATUS,
  INVENTORY_LEGEND_COLOR_MAP,
  sortOrderForSiteSummaryReport,
  ADDITIONAL_FILTER_SORT_ORDER,
} from '../constants';

export const sortData = (data, sortKey, order = 'desc') =>
  isArray(data) ? orderBy(data, sortKey, order) : data;

export const formatAssetData = (data, isInventoryAsset = false) => {
  const asset = {
    URGENT: 0,
    ONB: 0,
    CONN: 0,
    NODATA: 0,
    GOOD: 0,
    PLAN: 0,
    CRITICAL: 0,
    'NOT-CONN': 0,
    total: 0,
    existsAsset: 0,
    UAP: 0,
  };
  const inventoryAsset = {
    OUTOFDATE: 0,
    'ONB-INV': 0,
    'CONN-INV': 0,
    'NODATA-INV': 0,
    UPTODATE: 0,
    UPDAVBL: 0,
    'NOT-CONN-INV': 0,
    total: 0,
    existsAsset: 0,
    UAP: 0,
  };
  const requiredAsset = isInventoryAsset ? inventoryAsset : asset;
  const assetsAvailable = isInventoryAsset
    ? availableInventoryAsset
    : availableAsset;
  return data?.reduce((acc, feed) => {
    acc[feed?.id] = parseInt(feed?.count, 10);
    acc.total +=
      assetsAvailable.includes(feed?.id) && parseInt(feed?.count, 10);
    acc.existsAsset +=
      assetsAvailable.includes(feed?.id) && parseInt(feed?.count, 10) > 0 && 1;
    return acc;
  }, requiredAsset);
};

export const getInventoryLegend = data =>
  data
    ?.filter(assetSumEl => !EXCLUDED_STATUS.includes(assetSumEl?.id))
    .map(assetSumEl => ({
      key: assetSumEl?.id,
      color: INVENTORY_LEGEND_COLOR_MAP[assetSumEl?.id],
      label: assetSumEl?.dn,
    }));

const checkboxObj = (colorCode, radio = false) => ({
  color: colorCode,
  '& .MuiIconButton-label': {
    position: 'relative',
    zIndex: 0,
  },
  '&$checked': {
    color: colorCode,
  },
  '&:not($checked) .MuiIconButton-label:after': {
    content: '""',
    left: 2,
    top: 2,
    height: radio ? 13 : 12,
    width: radio ? 13 : 12,
    position: 'absolute',
    backgroundColor: !radio && colorCode,
    zIndex: -1,
    ...(radio && { borderRadius: '50%' }),
  },
  '&:not($checked).Mui-disabled .MuiIconButton-label:after': {
    content: '""',
    left: 2,
    top: 2,
    height: radio ? 13 : 12,
    width: radio ? 13 : 12,
    position: 'absolute',
    backgroundColor: !radio && '#e9e9e9',
    zIndex: -1,
    ...(radio && { borderRadius: '50%' }),
  },
});

const createStyledCheckbox = (key, qaId = '') => {
  const StyledCheckbox = props => (
    <Checkbox
      {...props}
      color="default"
      sx={checkboxObj(assetSummary?.[key]?.color)}
      {...(qaId && { 'data-qa-id': qaId })}
    />
  );
  return StyledCheckbox;
};

const createStyledRadio = (key, qaId = '') => {
  const StyledRadio = props => (
    <Radio
      {...props}
      color="default"
      sx={checkboxObj(assetSummary?.[key]?.color, true)}
      {...(qaId && { 'data-qa-id': qaId })}
    />
  );
  return StyledRadio;
};

export const GoodCheckbox = createStyledCheckbox('GOOD', 'checkbox-Good');
export const UrgentCheckbox = createStyledCheckbox('URGENT', 'checkbox-Urgent');
export const CriticalCheckbox = createStyledCheckbox(
  'CRITICAL',
  'checkbox-Critical',
);
export const PlanCheckbox = createStyledCheckbox('PLAN', 'checkbox-Plan');
export const YetCheckbox = createStyledCheckbox(
  'NOT-CONN',
  'checkbox-Yet-to-connect',
);
export const NoDataCheckbox = createStyledCheckbox(
  'NODATA',
  'checkbox-No-data',
);

export const GoodRadio = createStyledRadio('GOOD', 'checkbox-Good');
export const CriticalRadio = createStyledRadio('CRITICAL', 'checkbox-Critical');
export const UrgentRadio = createStyledRadio('URGENT', 'checkbox-Urgent');
export const PlanRadio = createStyledRadio('PLAN', 'checkbox-Plan');
export const YetRadio = createStyledRadio(
  'NOT-CONN',
  'checkbox-Yet-to-connect',
);
export const NoDataRadio = createStyledRadio('NODATA', 'checkbox-No-data');

export const statusSorting = (data, array = false, inventory = false) => {
  const sortOrderOption = inventory ? sortOrderInventory : sortOrder;
  if (array)
    data?.sort(
      (a, b) =>
        sortOrderOption[a?.id] - sortOrderOption[b?.id] ||
        a?.id?.localeCompare(b?.id),
    );
  else {
    data.sort(
      (a, b) => sortOrderOption[a] - sortOrderOption[b] || a?.localeCompare(b),
    );
  }
  return data;
};

export const additionalFilterSorting = data => {
  data?.sort(
    (a, b) => ADDITIONAL_FILTER_SORT_ORDER[a] - ADDITIONAL_FILTER_SORT_ORDER[b],
  );
  return data;
};

export const inventoryStatusSorting = data => {
  data?.sort((a, b) => sortOrderInventory[a.key] - sortOrderInventory[b.key]);
  return data;
};

export const statusObjSorting = data => {
  data.sort((a, b) => {
    const aData = assetSummary[a?.urgency?.id || a?.insightStatus?.id]?.state;
    const bData = assetSummary[b?.urgency?.id || b?.insightStatus?.id]?.state;
    return sortOrder[aData] - sortOrder[bData] || aData.localeCompare(bData);
  });
  return data;
};

export const statusObjSortingForSiteSummaryReport = data =>
  data
    .slice()
    .sort(
      (a, b) =>
        sortOrderForSiteSummaryReport[a] - sortOrderForSiteSummaryReport[b],
    );

export const getInventoryCheckboxStyles = colorCode => ({
  color: colorCode,
  '& .MuiIconButton-label': {
    position: 'relative',
    zIndex: 0,
  },
  '&$checked': {
    color: colorCode,
  },
  '&:not($checked) .MuiIconButton-label:after': {
    content: '""',
    left: 2,
    top: 2,
    height: 12,
    width: 12,
    position: 'absolute',
    backgroundColor: colorCode,
    zIndex: -1,
  },
  '&:not($checked).Mui-disabled .MuiIconButton-label:after': {
    content: '""',
    left: 2,
    top: 2,
    height: 12,
    width: 12,
    position: 'absolute',
    backgroundColor: '#e9e9e9',
    zIndex: -1,
  },
});

export const getInventoryFilterState = (inventoryLegends, equipStatusArr) => {
  const inventoryFilterState = {};
  inventoryLegends
    ?.map(legend => legend.key)
    .forEach(key => {
      inventoryFilterState[key] =
        (equipStatusArr?.length && equipStatusArr?.includes(key)) || false;
    });
  return inventoryFilterState;
};

export const calculateInPercentage = (
  numberOfEquipments = 1,
  onBoardedEquipments = 1,
) => {
  const percentage = (numberOfEquipments / onBoardedEquipments) * 100;
  return percentage % 1 !== 0 ? percentage.toFixed(1) : percentage;
};

export const addGAPAsset = (assetSums, dnLabel) => {
  const plan = assetSums?.find(item => item.id === 'PLAN');
  const good = assetSums?.find(item => item.id === 'GOOD');
  const gap = {
    id: 'GAP',
    ...(dnLabel && { dn: dnLabel }),
    count: (plan?.count || 0) + (good?.count || 0),
    woNp: (plan?.woNp || 0) + (good?.woNp || 0),
    woCmp: (plan?.woCmp || 0) + (good?.woCmp || 0),
    woInp: (plan?.woInp || 0) + (good?.woInp || 0),
  };
  return [...(assetSums || []), gap];
};

export const getWorkOrder = (sum, workOrderFilter) => {
  if (!workOrderFilter) return true;
  const { woCmp, woNp, woInp } = sum ?? {};
  const checks = {
    none: () => woNp >= 0 && woCmp === 0 && woInp === 0,
    some: () => woNp > 0 && (woCmp > 0 || woInp > 0),
    complete: () => woCmp > 0,
    inprogress: () => woInp > 0,
  };
  return checks[workOrderFilter]?.() ?? false;
};

export const getValueCalc = (assetData, sum, ptype, pvalue) => {
  if (!ptype) return true;
  const onBoarded = assetData?.find(ele => ele?.id === 'ONB')?.count ?? 0;
  const percentage = Number(calculateInPercentage(sum?.count, onBoarded));
  const valuePercentage = Number(pvalue);
  const operations = {
    equals: () => valuePercentage === percentage,
    nonequals: () => valuePercentage !== percentage,
    lessthan: () => percentage < valuePercentage,
    lessthanorequals: () => percentage <= valuePercentage,
    greaterthan: () => percentage > valuePercentage,
    greaterthanorequals: () => percentage >= valuePercentage,
  };
  return operations[ptype]?.() ?? false;
};

export const getStateDataFromDates = dates => {
  const stateData = {};

  dates?.forEach(dateObj => {
    (dateObj?.assetSum || []).forEach(asset => {
      const { id, dn, count = 0 } = asset;

      if (!stateData[id]) {
        stateData[id] = {
          id,
          dn,
          count: 0,
          woNp: 0,
          woCmp: 0,
          woInp: 0,
        };
      }
      stateData[id].count += count;
    });
  });

  return stateData;
};
